import * as Sentry from '@sentry/react'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { Headset } from 'lucide-react'
import React from 'react'

import { Button } from 'src/shared/components/ui/buttons/button'
import { Card, CardContent, CardFooter, CardTitle, CardDescription } from 'src/shared/components/ui/cards/card'
import { SupportDialog } from 'src/shared/features/support/components/support-dialog'
import { useToast } from 'src/shared/hooks/use-toast'

type ErrorFallbackStyle = 'none' | 'card' | 'alert' | 'custom'

export interface ErrorFallbackProps {
  error: Error
  resetErrorBoundary: () => void
  style?: ErrorFallbackStyle
  title?: string
  description?: string
  fallback?: React.ReactNode
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  style = 'card',
  title = 'Sorry, there was an error',
  description = 'Please try again later',
  fallback,
}) => {
  const { toast } = useToast()

  console.error(error)

  switch (style) {
    case 'none':
      return <></>
    case 'card':
      return (
        <Card>
          <CardContent className="pt-6">
            <CardTitle>{title}</CardTitle>
            <CardDescription className="mt-2">{description}</CardDescription>
          </CardContent>
          <CardFooter className="flex gap-3">
            <Button onClick={() => resetErrorBoundary()} variant="outline" className="w-full">
              Try again
            </Button>
            <SupportDialog
              trigger={
                <Button variant="default" className="w-full">
                  <Headset className="mr-2 size-4" />
                  Report Bug
                </Button>
              }
            />
          </CardFooter>
        </Card>
      )
    case 'alert':
      toast({
        title: title,
        description: description,
      })
      return <></>
    case 'custom':
      return <>{fallback}</>
    default:
      return <></>
  }
}

interface ErrorBoundaryWrapperProps {
  children: React.ReactNode
  title?: string
  description?: string
  style?: ErrorFallbackStyle
  fallback?: React.ReactNode
}

export const ErrorBoundaryWrapper: React.FC<ErrorBoundaryWrapperProps> = ({
  children,
  title,
  description,
  style = 'card',
  fallback,
}) => {
  const { reset } = useQueryErrorResetBoundary()

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback
          error={error as Error}
          resetErrorBoundary={resetError}
          style={style}
          title={title}
          description={description}
          fallback={fallback}
        />
      )}
      onReset={reset}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
